import { KeyboardEvent, MouseEvent, ReactNode } from "react";

interface IconClickableProps {
  className?: string;
  onClick: (e: MouseEvent | KeyboardEvent) => void;
  testId?: string;
  ariaLabel?: string;
  disabled?: boolean;
  title?: string;
  children: ReactNode;
}

/**
 * Keyboard-accessible click target for the lineage canvas controls
 * (expand/collapse handles, expansion arrows). Renders a div styled like the
 * surrounding controls but with button semantics so it satisfies a11y rules.
 * Pass `ariaLabel` to give the button an accessible name (also lets tests use
 * semantic `getByRole("button", { name })` queries).
 *
 * `disabled` marks the control unavailable: it stops firing `onClick`, drops out
 * of the tab order and exposes `aria-disabled`. Callers are expected to pair it
 * with a disabled style so the control also *looks* inert — a control that
 * silently no-ops while still looking clickable reads as a broken UI.
 */
const IconClickable = ({
  className,
  onClick,
  testId,
  ariaLabel,
  disabled = false,
  title,
  children,
}: IconClickableProps) => (
  <div
    role="button"
    tabIndex={disabled ? -1 : 0}
    aria-label={ariaLabel}
    aria-disabled={disabled}
    title={title}
    className={className}
    onClick={e => {
      if (disabled) return;
      onClick(e);
    }}
    onKeyDown={e => {
      if (disabled) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onClick(e);
      }
    }}
    data-testid={testId}
  >
    {children}
  </div>
);

export default IconClickable;
